import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Table, Tag, Space, Switch, Button, Tooltip, Typography, Drawer, Input, Popconfirm, Alert, Statistic, Row, Col, Segmented, message } from 'antd';
import { ReloadOutlined, PlayCircleOutlined, HistoryOutlined, RedoOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import apiClient from '../../../shared/services/apiClient';

const { Text, Paragraph } = Typography;
const call = async (fn) => {
    try {
        return (await fn()).data;
    } catch (e) {
        return { success: false, error: e.response?.data?.error || e.message };
    }
};
const STATE_COLOR = { up: 'success', busy: 'warning', stuck: 'error', down: 'error' };
const every = (s) => (!s ? '-' : s % 86400 === 0 ? `${s / 86400}d` : s % 3600 === 0 ? `${s / 3600}h` : s % 60 === 0 ? `${s / 60}m` : `${s}s`);
const ago = (ts) => {
    if (!ts) return '-';
    const d = Math.round((Date.now() - new Date(ts.replace(' ', 'T')).getTime()) / 1000);
    return d < 60 ? `${d}s` : d < 3600 ? `${Math.round(d / 60)}m` : d < 86400 ? `${Math.round(d / 3600)}h` : `${Math.round(d / 86400)}d`;
};
const taskBad = (t) => t.consecutive_failures > 0 || t.overdue;

/** 后台进程 + 定时任务:进程心跳/内存/卡在哪个任务;任务最近执行、结果、日志;启停、立即执行、重启进程 */
const Processes = () => {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState('all');
    const [q, setQ] = useState('');
    const [history, setHistory] = useState(null); // { task, items, total, page }

    const load = useCallback(async () => {
        setLoading(true);
        const r = await call(() => apiClient.get('/api/admin/system/processes'));
        setLoading(false);
        if (r.success) setData(r.data); else message.error(r.error || t('Failed to load'));
    }, [t]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        const id = setInterval(load, 15000);
        return () => clearInterval(id);
    }, [load]);

    const openHistory = async (task, page = 1, status = '') => {
        const r = await call(() => apiClient.get('/api/admin/system/tasks/runs', { params: { task, page, status } }));
        if (r.success) setHistory({ task, status, ...r.data }); else message.error(r.error || t('Failed to load'));
    };
    const toggle = async (task, enabled) => {
        const r = await call(() => apiClient.post('/api/admin/system/tasks/toggle', { name: task.name, enabled }));
        if (r.success) load(); else message.error(r.error || t('Save failed'));
    };
    const runNow = async (task) => {
        const r = await call(() => apiClient.post('/api/admin/system/tasks/run', { name: task.name }));
        if (r.success) { message.success(t('Will run within 5 seconds')); setTimeout(load, 6000); } else message.error(r.error || t('Failed'));
    };
    const restart = async (p) => {
        const r = await call(() => apiClient.post('/api/admin/system/processes/restart', { name: p.name }));
        if (r.success) { message.success(t('Restarting')); setTimeout(load, 8000); } else message.error(r.error || t('Failed'));
    };

    const procs = useMemo(() => {
        const kw = q.trim().toLowerCase();
        return (data?.processes || []).filter((p) => {
            const hit = !kw || p.name.toLowerCase().includes(kw) || p.tasks.some((x) => x.name.toLowerCase().includes(kw));
            const bad = p.state !== 'up' || p.tasks.some(taskBad);
            return hit && (filter === 'all' || bad);
        });
    }, [data, filter, q]);

    const taskColumns = [
        {
            title: t('Task'), dataIndex: 'name',
            render: (v, r) => <Space size={4}><Text code>{v}</Text>{!r.enabled && <Tag>{t('Disabled')}</Tag>}{r.overdue && <Tag color="warning">{t('Not run on time')}</Tag>}</Space>,
        },
        { title: t('Every'), dataIndex: 'interval_sec', width: 80, render: every },
        {
            title: t('Last run'), dataIndex: 'last_started_at', width: 120,
            render: (v, r) => (
                <Tooltip title={v}>
                    <Space size={4}>
                        {r.last_status === 'ok' && <Tag color="success">OK</Tag>}
                        {r.last_status === 'failed' && <Tag color="error">{t('Failed')}{r.consecutive_failures > 1 ? ` ×${r.consecutive_failures}` : ''}</Tag>}
                        {r.last_status === 'running' && <Tag color="processing">{t('Running')}</Tag>}
                        {ago(v)}
                    </Space>
                </Tooltip>
            ),
        },
        { title: t('Duration'), dataIndex: 'last_duration_ms', width: 90, render: (v) => (v == null ? '-' : v >= 1000 ? `${(v / 1000).toFixed(1)}s` : `${v}ms`) },
        {
            title: t('Result'), key: 'result',
            render: (_, r) => (r.last_status === 'failed' && r.last_error
                ? <Tooltip title={<pre style={{ whiteSpace: 'pre-wrap', margin: 0, maxHeight: 300, overflow: 'auto' }}>{r.last_error}</pre>}><Text type="danger" ellipsis style={{ maxWidth: 360 }}>{r.last_error.split('\n')[0]}</Text></Tooltip>
                : <Text type="secondary" ellipsis style={{ maxWidth: 360 }}>{r.last_summary || ''}</Text>),
        },
        { title: t('Runs'), key: 'runs', width: 100, render: (_, r) => <Text type="secondary">{r.runs_ok}{r.runs_failed ? <Text type="danger"> / {r.runs_failed}</Text> : ''}</Text> },
        {
            title: '', key: 'op', width: 150,
            render: (_, r) => (
                <Space size={4}>
                    <Tooltip title={t('Enabled')}><Switch size="small" checked={r.enabled} onChange={(v) => toggle(r, v)} /></Tooltip>
                    <Tooltip title={t('Run now')}><Button size="small" type="text" icon={<PlayCircleOutlined />} onClick={() => runNow(r)} /></Tooltip>
                    <Tooltip title={t('History')}><Button size="small" type="text" icon={<HistoryOutlined />} onClick={() => openHistory(r.name)} /></Tooltip>
                </Space>
            ),
        },
    ];

    const procColumns = [
        {
            title: t('Process name'), dataIndex: 'name',
            render: (v, p) => (
                <Space direction="vertical" size={0}>
                    <Space size={4}><Tag color={STATE_COLOR[p.state]}>{t(`proc_${p.state}`)}</Tag><Text strong>{v}</Text></Space>
                    {p.description && <Text type="secondary" style={{ fontSize: 12 }}>{p.description}</Text>}
                </Space>
            ),
        },
        { title: t('Workers'), key: 'w', width: 90, render: (_, p) => <Text type={p.alive < p.count ? 'danger' : undefined}>{p.alive}/{p.count}</Text> },
        { title: t('Memory'), dataIndex: 'mem_mb', width: 90, render: (v) => (v == null ? '-' : `${v} MB`) },
        { title: t('Up since'), dataIndex: 'started_at', width: 90, render: (v) => <Tooltip title={v}>{ago(v)}</Tooltip> },
        {
            title: t('Heartbeat'), dataIndex: 'heartbeat_age', width: 200,
            render: (v, p) => (
                <Space direction="vertical" size={0}>
                    <Text type={v > 60 ? 'warning' : undefined}>{v == null ? '-' : `${v}s`}</Text>
                    {p.running_task && <Text type="secondary" style={{ fontSize: 12 }}>{t('Running')}: {p.running_task}</Text>}
                </Space>
            ),
        },
        {
            title: t('Tasks'), key: 'tasks', width: 110,
            render: (_, p) => {
                const bad = p.tasks.filter(taskBad).length;
                return <Space size={4}>{p.tasks.length}{bad > 0 && <Tag color="error">{bad}</Tag>}</Space>;
            },
        },
        {
            title: '', key: 'op', width: 60,
            render: (_, p) => p.restartable && p.monitored && (
                <Popconfirm title={t('Restart this process? Running work is interrupted.')} onConfirm={() => restart(p)}>
                    <Tooltip title={t('Restart')}><Button size="small" type="text" danger icon={<RedoOutlined />} /></Tooltip>
                </Popconfirm>
            ),
        },
    ];

    const s = data?.summary;
    return (
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
            <Card
                title={t('Background processes')}
                extra={(
                    <Space wrap>
                        <Input.Search size="small" allowClear placeholder={t('Search')} onChange={(e) => setQ(e.target.value)} style={{ width: 180 }} />
                        <Segmented size="small" value={filter} onChange={setFilter} options={[{ value: 'all', label: t('All') }, { value: 'bad', label: t('Problems') }]} />
                        <Button size="small" icon={<HistoryOutlined />} onClick={() => openHistory('', 1, 'failed')}>{t('Recent failures')}</Button>
                        <Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading} />
                    </Space>
                )}
            >
                {data?.alerts?.length > 0 && (
                    <Alert type="error" showIcon style={{ marginBottom: 16 }} message={t('Active alerts')}
                        description={data.alerts.map((a) => <div key={a}><Text code>{a}</Text></div>)} />
                )}
                {s && (
                    <Row gutter={16}>
                        <Col xs={12} md={4}><Statistic title={t('Processes')} value={s.processes} /></Col>
                        <Col xs={12} md={4}><Statistic title={t('Down')} value={s.down} valueStyle={s.down ? { color: '#cf1322' } : undefined} /></Col>
                        <Col xs={12} md={4}><Statistic title={t('Busy / stuck')} value={s.stuck} valueStyle={s.stuck ? { color: '#d48806' } : undefined} /></Col>
                        <Col xs={12} md={4}><Statistic title={t('Tasks')} value={s.tasks} /></Col>
                        <Col xs={12} md={4}><Statistic title={t('Failing')} value={s.failing} valueStyle={s.failing ? { color: '#cf1322' } : undefined} /></Col>
                        <Col xs={12} md={4}><Statistic title={t('Not run on time')} value={s.overdue} valueStyle={s.overdue ? { color: '#d48806' } : undefined} /></Col>
                    </Row>
                )}
            </Card>

            <Card size="small">
                <Table
                    rowKey="name" size="small" loading={loading && !data} columns={procColumns} dataSource={procs} pagination={false} scroll={{ x: 900 }}
                    expandable={{
                        rowExpandable: (p) => p.tasks.length > 0,
                        defaultExpandAllRows: false,
                        expandedRowRender: (p) => <Table rowKey="name" size="small" columns={taskColumns} dataSource={p.tasks} pagination={false} scroll={{ x: 900 }} />,
                    }}
                />
            </Card>

            <Drawer width={Math.min(960, window.innerWidth)} open={!!history} onClose={() => setHistory(null)}
                title={history?.task ? history.task : t('Recent failures')}>
                {history && (
                    <Table
                        rowKey="id" size="small" dataSource={history.items}
                        pagination={{ current: history.page, pageSize: history.per_page, total: history.total, onChange: (pg) => openHistory(history.task, pg, history.status) }}
                        columns={[
                            { title: t('Time'), dataIndex: 'started_at', width: 160 },
                            ...(history.task ? [] : [{ title: t('Task'), dataIndex: 'task', render: (v) => <Text code>{v}</Text> }]),
                            { title: '', dataIndex: 'trigger', width: 70, render: (v) => (v === 'manual' ? <Tag>{t('Manual run')}</Tag> : null) },
                            { title: t('Status'), dataIndex: 'status', width: 80, render: (v) => <Tag color={v === 'ok' ? 'success' : 'error'}>{v === 'ok' ? 'OK' : t('Failed')}</Tag> },
                            { title: t('Duration'), dataIndex: 'duration_ms', width: 90, render: (v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}s` : `${v}ms`) },
                            { title: t('Result'), key: 'r', render: (_, r) => <Text type={r.status === 'ok' ? 'secondary' : 'danger'} ellipsis style={{ maxWidth: 320 }}>{r.status === 'ok' ? r.summary : (r.error || '').split('\n')[0]}</Text> },
                        ]}
                        expandable={{
                            rowExpandable: (r) => !!(r.error || r.summary || r.logs?.length),
                            expandedRowRender: (r) => (
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    {r.summary && <Paragraph copyable={{ text: r.summary }} style={{ margin: 0 }}><Text code>{r.summary}</Text></Paragraph>}
                                    {r.error && <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontSize: 12 }}>{r.error}</pre>}
                                    {r.logs?.length > 0 && (
                                        <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontSize: 12, maxHeight: 320, overflow: 'auto' }}>
                                            {r.logs.map((l) => `${l.t} ${l.l.padEnd(7)} ${l.m}`).join('\n')}
                                        </pre>
                                    )}
                                    <Text type="secondary" style={{ fontSize: 12 }}>{r.process} · pid {r.pid}</Text>
                                </Space>
                            ),
                        }}
                    />
                )}
            </Drawer>
        </Space>
    );
};

export default Processes;
